import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Badge,
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Add, StarBorder } from '@mui/icons-material';
import { IntegracaoContext } from 'contexts/IntegracaoContext';
import { CustomPagination, encryptURL } from 'utils/functions';
import { logos } from 'assets/integracoes';
import { useModal } from 'components/Modals';
import Container from 'components/Container';
import Header from 'components/Header';
import DeleteModal from 'components/Modals/DeleteModal';
import AddModal from './Modals/AddModal';
import Blank from 'assets/blank.png';
import localeText from 'utils/localeText';
import styles from './styles';

const Integrações = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState(0);
  const [integracoes, setIntegracoes] = useState([]);
  const {
    getLoading,
    postLoading,
    deleteLoading,
    getIntegracao,
    postIntegracao,
    deleteIntegracao,
  } = useContext(IntegracaoContext);
  const loading = getLoading || postLoading || deleteLoading;

  useEffect(() => {
    fetchData();
  }, []);

  const opcoes = [
    {
      titulo: 'Bancos',
      integracoes: [
        { nome: 'Banco do Brasil', api: 'BBRASIL' },
        { nome: 'Bradesco', api: 'BRADESCO' },
        { nome: 'Caixa', api: 'CEF' },
        { nome: 'Itaú', api: 'ITAU' },
        { nome: 'Safra', api: 'SAFRA' },
        { nome: 'Santander', api: 'SANTANDER' },
        { nome: 'Sicoob', api: 'SICOOB' },
        { nome: 'Sicredi', api: 'SICRED' },
        { nome: 'Sisprime', api: 'SISPRIME' },
      ],
    },
    {
      titulo: 'Canais de Venda',
      integracoes: [
        { nome: 'Amazon', api: 'AMAZON' },
        { nome: 'Bling', api: 'BLING' },
        { nome: 'Kwai', api: 'KWAI' },
        { nome: 'Madeira Madeira', api: 'MADEIRA MADEIRA' },
        { nome: 'Magalu', api: 'MAGAZINE LUIZA' },
        { nome: 'Mercado Livre', api: 'MERCADO LIVRE' },
        { nome: 'Mercos', api: 'MERCOS' },
        { nome: 'Nuvemshop', api: 'NUVEMSHOP' },
        { nome: 'Omie', api: 'OMIE' },
        { nome: 'Shein', api: 'SHEIN' },
        { nome: 'Shopee', api: 'SHOPEE' },
        { nome: 'Shopify', api: 'SHOPIFY' },
        { nome: 'Tiktok', api: 'TIKTOK' },
        { nome: 'Tiny', api: 'TINY' },
        { nome: 'Tray', api: 'TRAY' },
        { nome: 'Woo Commerce', api: 'WOO' },
      ],
    },
    {
      titulo: 'Outros',
      integracoes: [
        { nome: 'Eprom Emissor', api: 'INCUBADORA_E' },
        { nome: 'Eprom Receptor', api: 'INCUBADORA_R' },
        { nome: 'GPT Maker', api: 'GPTMAKER' },
        { nome: 'WhatsApp', api: 'WHATSAPP' },
      ],
    },
  ];

  const fetchData = () => {
    const cb = (res) => {
      setIntegracoes(res);
      closeModal();
    };

    getIntegracao({ cb });
  };

  const handleIntegracao = (data) => {
    postIntegracao({ data, cb: fetchData });
  };

  const handleEdit = (item) => {
    // WhatsApp credentials from the generic endpoint must not enter the URL.
    const hash = encryptURL(
      item.api === 'WHATSAPP' ? { id: item.id, api: item.api } : item
    );
    navigate(`/app/Integracoes/Manutencao/${hash}`);
  };

  const handleDelete = (item) => {
    openModal(
      <DeleteModal
        onSubmit={() => deleteIntegracao({ id: item?.id, cb: fetchData })}
      />
    );
  };

  const IntegracaoCard = ({ item }) => {
    const nome_logo = item?.api?.replace(/\s+/g, '');
    const logo = logos?.[nome_logo] ?? Blank;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
        <Card elevation={3}>
          <CardActionArea
            sx={styles?.card}
            onClick={() =>
              openModal(
                <AddModal item={item} logo={logo} onSubmit={handleIntegracao} />
              )
            }
          >
            <Box src={logo} component="img" alt={item?.nome} sx={styles?.img} />
          </CardActionArea>
        </Card>
      </Grid>
    );
  };

  const IntegracaoCell = ({ row }) => {
    const integracao = opcoes
      ?.flatMap((o) => o?.integracoes)
      ?.find((i) => i?.api === row?.api);

    const nome_logo = integracao?.api?.replace(/\s+/g, '');
    const logo = logos?.[nome_logo] ?? Blank;

    return (
      <Box p={1}>
        <Box
          src={logo}
          component="img"
          sx={styles?.img}
          alt={integracao?.nome}
        />
      </Box>
    );
  };

  return (
    <Container fillHeight sx={styles?.container}>
      <Header titulo="Integrações" />
      <Grid container spacing={2} sx={styles?.content}>
        <Grid item sx={styles?.contentItem}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab
              value={0}
              icon={<StarBorder />}
              iconPosition="start"
              label={
                <Box display="flex" gap={2}>
                  Minhas Integrações
                  {integracoes?.length > 0 && (
                    <Badge badgeContent={integracoes?.length} color="error" />
                  )}
                </Box>
              }
            />
            <Tab
              value={1}
              icon={<Add />}
              iconPosition="start"
              label="Integrações Disponíveis"
            />
          </Tabs>
        </Grid>
        <Grid item display={tab ? 'none' : 'flex'} sx={styles?.fillItem}>
          <DataGrid
            sx={styles?.dataGrid}
            rows={integracoes}
            columns={[
              {
                field: 'actions',
                headerName: 'Ações',
                type: 'actions',
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    icon={<Icon>settings</Icon>}
                    label="Editar"
                    onClick={() => handleEdit(row)}
                  />,
                  <GridActionsCellItem
                    icon={<Icon>delete</Icon>}
                    label="Excluir"
                    onClick={() => handleDelete(row)}
                  />,
                ],
              },
              {
                field: 'integracao',
                headerName: 'Integração',
                width: 200,
                renderCell: (props) => <IntegracaoCell {...props} />,
              },
              {
                field: 'nome',
                headerName: 'Nome',
                minWidth: 300,
                flex: 1,
                sortable: false,
                valueGetter: ({ row }) =>
                  row?.access_data?.nome_integracao ??
                  row?.access_data?.nome_loja ??
                  '',
              },
            ]}
            pagination
            density="compact"
            disableColumnMenu
            showCellRightBorder
            loading={loading}
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{
              Pagination: CustomPagination,
              LoadingOverlay: LinearProgress,
            }}
          />
        </Grid>
        <Grid item display={tab ? 'block' : 'none'} sx={styles?.scrollItem}>
          <Grid container spacing={10}>
            {opcoes?.map(({ integracoes = [], titulo = '' }, index) => (
              <Grid item xs={12} key={`grupo-${index}`}>
                <Typography
                  mb={2}
                  variant="h4"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {titulo}
                </Typography>
                <Grid container spacing={2} sx={styles?.scrollContent}>
                  {integracoes?.map((item, _index) => (
                    <IntegracaoCard
                      item={item}
                      key={`integracao-${index}-${_index}`}
                    />
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Integrações;
